import { createContext, useContext, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { apiClient } from '@/services/apiClient';
import type { DashboardSummaryDto } from '@/hooks/useDashboardSummary';

interface DashboardSummaryContextType {
  summary: DashboardSummaryDto | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const DashboardSummaryContext = createContext<DashboardSummaryContextType | undefined>(undefined);

interface DashboardSummaryProviderProps {
  children: ReactNode;
}

export function DashboardSummaryProvider({ children }: DashboardSummaryProviderProps) {
  const { currentEmployee } = useTimesheetForScreen('homepage');
  const { getScreenMode } = useDataSource();
  const [summary, setSummary] = useState<DashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    const mode = getScreenMode('homepage');
    if (mode !== 'live') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await apiClient.get<DashboardSummaryDto>(
        `/dashboard/summary?employeeId=${encodeURIComponent(String(currentEmployee.id))}`
      );
      setSummary(data);
    } catch (err) {
      console.error('Error fetching dashboard summary:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [currentEmployee.id, getScreenMode]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Refetch when the browser tab regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSummary();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSummary]);

  // Listen for company-changed event to refetch data when switching companies.
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const handleCompanyChange = (event: Event) => {
      clearTimeout(debounceTimer);
      const signal = (event as CustomEvent).detail?.signal as AbortSignal | undefined;

      debounceTimer = setTimeout(() => {
        if (signal?.aborted) return;
        fetchSummary();
      }, 150);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [fetchSummary]);

  // Refetch when a timesheet is submitted so submission consistency stats stay current.
  useEffect(() => {
    const handleTimesheetSubmitted = () => {
      fetchSummary();
    };

    window.addEventListener('timesheet-submitted', handleTimesheetSubmitted);
    return () => window.removeEventListener('timesheet-submitted', handleTimesheetSubmitted);
  }, [fetchSummary]);

  return (
    <DashboardSummaryContext.Provider value={{ summary, loading, error, refetch: fetchSummary }}>
      {children}
    </DashboardSummaryContext.Provider>
  );
}

export function useDashboardSummaryContext(): DashboardSummaryContextType {
  const context = useContext(DashboardSummaryContext);
  if (context === undefined) {
    throw new Error('useDashboardSummaryContext must be used within a DashboardSummaryProvider');
  }
  return context;
}
